import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebase';

const MARKET_DATA_COLLECTION = 'marketData';
const DOC_IDS = ['commodities', 'currency', 'global_indices', 'indian_indices', 'nifty_stocks', 'news'];

/**
 * Recursively convert Firestore Timestamps to ISO strings for JSON-safe data.
 * @param {*} obj - Value (object, array, or primitive)
 * @returns {*} - Same structure with Timestamps as ISO strings
 */
function serializeTimestamps(obj) {
    if (obj === null || obj === undefined) return obj;
    if (typeof obj === 'object' && obj.toDate && typeof obj.toDate === 'function') {
        return obj.toDate().toISOString();
    }
    if (Array.isArray(obj)) {
        return obj.map((item) => serializeTimestamps(item));
    }
    if (typeof obj === 'object') {
        const out = {};
        for (const [key, value] of Object.entries(obj)) {
            out[key] = serializeTimestamps(value);
        }
        return out;
    }
    return obj;
}

/**
 * Fetches marketData collection from Firestore.
 * Returns a keyed object: { currency, commodities, global_indices, indian_indices, nifty_stocks }.
 * @returns {{ data: Object|null, loading: boolean, error: string|null, refetch: function }}
 */
export function useMarketData() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const ref = collection(db, MARKET_DATA_COLLECTION);
            const snapshot = await getDocs(ref);
            const keyed = {};
            snapshot.forEach((docSnap) => {
                const raw = docSnap.data();
                keyed[docSnap.id] = serializeTimestamps(raw);
            });
            // Ensure all expected doc IDs exist (may be undefined if missing)
            DOC_IDS.forEach((id) => {
                if (!(id in keyed)) keyed[id] = null;
            });
            setData(keyed);
        } catch (err) {
            console.error('useMarketData:', err);
            setError(err?.message || 'Failed to load market data.');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    return { data, loading, error, refetch: loadData };
}

export default useMarketData;
